import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, concat, empty, from, Observable, of, Subject } from 'rxjs';
import { ServiceHelper } from '../config';
import { AuctionLotModel, AuctionLotSearchFilters, AuctionModel, BidMadeResponseModel, BidRequestModel, FilterItemValue, MyAuctionLotModel, PagedResult } from '../models';
import { AppConfig } from '../config';
import { AuctionLotSearchFilterItemChoicesModel, AuctionLotSearchFilterItemRangeModel, AuctionLotSearchFilterItemTextModel } from '../auction/models';
import { AuctionLotSearchSortType } from '../models/auction-lot-search-sort-type';
import { HubConnection } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuctionService, MyAuctionLotService, StateService } from '.';
import * as moment from 'moment';

@Injectable()
export class AuctionLotService {

    constructor(public http: HttpClient, public stateService: StateService, public serviceHelper: ServiceHelper, private auctionService: AuctionService, private myAuctionLotService: MyAuctionLotService, private appConfig: AppConfig) {
        var me = this;
    }

    connection: HubConnection
    connectionReady: Promise<void>;

    bidMade$: Subject<BidMadeResponseModel> = new Subject<BidMadeResponseModel>();
    previousWatchIds: number[] = [];

    auctionId: number;
    auctionIdTrigger$ = new BehaviorSubject<number>(0);
    sortBy: AuctionLotSearchSortType = AuctionLotSearchSortType.LotNumber;
    filters: AuctionLotSearchFilters = this.GetDefaultFilters();
    page: number = 1;
    pageSize: number = 30;

    searchStream$ = new BehaviorSubject<AuctionLotSearchFilters>(this.filters);
    obs$ = this.searchStream$.pipe(
        switchMap((filters) =>
            concat(
                of({ type: 'start', value: null }),
                this.Search(this.auctionId ?? this.auctionService.auction?.Id, filters, this.sortBy, this.page, this.pageSize).pipe(map(value => ({ type: 'finish', value })))
            )),
        tap(res => {
            if (res.type === 'finish') {
                var results = res.type === 'finish' ? res.value : [];
                // console.log('after', results);
                // bind new Watch event for this page of results
                this.connectionReady.then(x => {
                    // console.log('connection ready, sending watch', results.Queryable.map(x => x.Id), this.previousWatchIds);
                    this.connection?.send('watchAuctionLots', results.Queryable.map(x => x.Id), this.previousWatchIds);
                    this.previousWatchIds = results.Queryable.map(x => x.Id); // Set the watch ids for next time
                });
                results.Queryable.forEach(element => {
                    if (element.CurrentBidDateTime) element.CurrentBidDateTime = moment.tz(element.CurrentBidDateTime, 'Australia/Melbourne').toDate();
                });
                //return results;
            }
        }),
    );

    AuctionChanged() {
        let me = this;
        me.auctionIdTrigger$.next(me.auctionId);
    }

    AddToWatchList(auctionLotId: number): Observable<any> {
        let me = this;

        return me.myAuctionLotService.AddToWatchList([auctionLotId], me.connection.connectionId);

        //return from(me.connection.send('addToWatchList', auctionLotId).then(x => console.log('added to watch list')));
    }

    RemoveFromWatchList(auctionLotId: number): Observable<any> {
        let me = this;

        return me.myAuctionLotService.RemoveFromWatchList([auctionLotId], me.connection.connectionId);
        //return from(me.connection.send('removeFromWatchList', auctionLotId).then(x => console.log('removed from watch list')));
    }

    // AddToWatchList(auctionLotId: number): Observable<any> {
    //     let me = this;
    //     return from(me.connection.send('addToWatchList', auctionLotId).then(x => console.log('added to watch list')));
    // }

    // RemoveFromWatchList(auctionLotId: number): Observable<any> {
    //     let me = this;
    //     return from(me.connection.send('removeFromWatchList', auctionLotId).then(x => console.log('removed from watch list')));
    // }

    Initialize() {
        let me = this;
        // console.log('Initialize');

        this.myAuctionLotService.UpdateList(); // trigger my auction lots update

        if (!this.connection) {
            this.connection = new signalR.HubConnectionBuilder()
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .withHubProtocol(new signalR.JsonHubProtocol())
                .withUrl(this.appConfig.getConfig('apiUrl') + '/bidHub', { accessTokenFactory: () => this.serviceHelper.GetBearerToken() })
                .build();
            me.stateService.userModel$.subscribe(x => {
                // console.log('User model changed, reconnecting');
                me.connection.stop().then(x => me.connection.start()).then(x => console.log('reconnected'));
            });
        }

        if (me.connection?.state == signalR.HubConnectionState.Disconnected) {
            // console.log('bind');
            // Only set the event on the first load
            me.connection.on('bidMade', (Id, CurrentBidDateTime, CurrentBid, CurrentBidCustomerId, NextBid, Bids, Bidders, Watchers) => {
                var response = <BidMadeResponseModel>{
                    Id,
                    CurrentBidDateTime: CurrentBidDateTime ? moment.tz(CurrentBidDateTime, 'Australia/Melbourne').toDate() : null,
                    CurrentBid,
                    CurrentBidCustomerId,
                    NextBid,
                    Bids,
                    Bidders,
                    Watchers,
                };
                // console.log('bidMade', response);

                me.bidMade$.next(response);
            });

            me.connection.on('myAuctionLotUpdated', (Id, BidDate, BidAmount, IsWinning, TempBidAmount, TempAutoBidAmount, TempBuyerPremiumAmount, MinimumNextBid, AutoBidAmount, WatchingOnly) => {
                var response = <MyAuctionLotModel>{
                    Id,
                    BidDate: BidDate ? moment.tz(BidDate, 'Australia/Melbourne').toDate() : null,
                    BidAmount,
                    IsWinning,
                    TempBidAmount,
                    TempAutoBidAmount,
                    TempBuyerPremiumAmount,
                    MinimumNextBid,
                    AutoBidAmount,
                    WatchingOnly,
                };
                // console.log('myAuctionLotUpdated', response);

                me.myAuctionLotService.UpdateItem(response);
            });

            me.connectionReady = me.connection.start();
        } else {
            me.connectionReady = of(null).toPromise();
        }
        me.connectionReady.then(val => {
            // console.log('connected', me.connection);
        }).catch(err => {
            console.error(err);
            document.write(err);
        });
    }

    FindVintages(auctionId: number): Observable<number[]> {
        var headers = this.serviceHelper.GetHeaders();

        return this.http.get<number[]>(this.appConfig.getConfig('apiUrl') + '/vintages/' + (auctionId ? `auction/${auctionId}` : ''), { withCredentials: true, headers: headers });
    }

    GetVintageOptions(): FilterItemValue[] {
        let options = [];
        for (let year = 1920; year < new Date().getFullYear(); year += 10) {
            let val = `${year} - ${year + 10}`;
            options.push(<FilterItemValue>{ value: val, label: val });
        }
        return options;
    }

    GetDefaultFilters(): AuctionLotSearchFilters {
        return <AuctionLotSearchFilters>{
            keyword: <AuctionLotSearchFilterItemTextModel>{
                label: 'Keyword',
                placeholder: 'Search by wine, varietal, region, vintage etc',
                value: '',
            },
            category: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Category',
                placeholder: 'Search categories',
                options: [],
                values: [],
                searchable: true,
                validation: true,
            },
            lotNumber: <AuctionLotSearchFilterItemTextModel>{
                label: 'Lot Number',
                placeholder: 'Enter the lot number (E.g. 1-5, 6-10)',
                value: '',

                regex: /^[0-9\-,]*$/g,
                regexError: 'Only numbers, spaces, commas and dashes are allowed.',
            },
            price: <AuctionLotSearchFilterItemRangeModel>{
                label: 'Price Range',
                helpText: 'Enter your desired price range, based on Estimate per unit',
                minValue: null,
                maxValue: null,
                value: null,
                prefix: '$',
                floor: 0,
                ceiling: 1000,
            },
            country: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Country',
                placeholder: 'Search country',
                options: [],
                values: [],
                searchable: true,
                validation: true,
            },
            region: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Region',
                placeholder: 'Search region',
                options: [],
                values: [],
                searchable: true,
                validation: true,
            },
            vintage: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Vintage',
                placeholder: 'Search vintage',
                options: [],
                values: [],
            },
            variety: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Variety',
                placeholder: 'Search variety',
                options: [],
                values: [],
                searchable: true,
                validation: true,
            },
            unitOfMeasure: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Size',
                placeholder: 'Search size',
                options: [],
                values: [],
                searchable: true,
            },
            winery: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Winery',
                placeholder: 'Search winery',
                options: [],
                values: [],
                searchable: true,
            },
            excludeAttribute: <AuctionLotSearchFilterItemChoicesModel>{
                label: 'Exclude conditions',
                helpText: 'Do not display wines marked with the following:',
                options: [],
                values: [],
            },
        };
    }

    Search(auctionId: number, filters: AuctionLotSearchFilters, sortBy: AuctionLotSearchSortType, page: number, pageSize: number): Observable<any> {
        let me = this;
        var headers = this.serviceHelper.GetHeaders();

        if (!auctionId) {
            console.warn('AuctionId is empty');
            return;
        }

        var params = JSON.stringify({
            auctionId,
            filters,
            sortBy,
            page,
            pageSize,
        });

        // console.log('search', auctionId);

        return this.http.post<PagedResult<AuctionLotModel>>(this.appConfig.getConfig('apiUrl') + '/auction-lots/search', params, { withCredentials: true, headers: headers });
    }

    SubmitBids(bids: BidRequestModel[]): Observable<any> {
        let me = this;
        var headers = this.serviceHelper.GetHeaders();

        var params = JSON.stringify(bids);

        // console.log('SubmitBids', bids);

        return this.http.post<any>(this.appConfig.getConfig('apiUrl') + '/auction-lots/submit-bids', params, { withCredentials: true, headers: headers });
    }
}